import { Fragment, useState } from "react";
import { useAdminBillingEvents, useReplayBillingEvent } from "../../hooks/use-admin.js";
import {
  downloadBillingExport,
  type BillingExportType,
} from "../../api/admin.js";
import { Skeleton } from "../../components/skeleton.js";

const EVENT_TYPES = [
  "checkout.session.completed",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "customer.subscription.trial_will_end",
  "invoice.paid",
  "invoice.payment_failed",
];

const DATE_RANGES: Array<{ label: string; days: number | null }> = [
  { label: "All time", days: null },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export function AdminBillingEventsPage() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [dateRange, setDateRange] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  async function handleExport(type: BillingExportType) {
    setDownloading(type);
    try {
      await downloadBillingExport(type);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Export failed");
    } finally {
      setDownloading(null);
    }
  }

  const params: {
    page: number;
    limit: number;
    type?: string;
    fromDate?: string;
  } = { page, limit: 50 };
  if (typeFilter) params.type = typeFilter;
  if (dateRange) params.fromDate = daysAgo(dateRange);

  const { data, isLoading } = useAdminBillingEvents(params);
  const replayMutation = useReplayBillingEvent();

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setTypeFilter(e.target.value);
    setPage(1);
  }

  function handleDateChange(days: number | null) {
    setDateRange(days);
    setPage(1);
  }

  return (
    <div className="product-page">
      <h1 className="page-title">Billing Events</h1>

      <section className="product-section">
        <div className="filter-bar">
          <div>
            <label
              htmlFor="event-type-filter"
              className="filter-label"
            >
              Event type:
            </label>
            <select
              id="event-type-filter"
              value={typeFilter}
              onChange={handleTypeChange}
              style={{
                padding: "6px 10px",
                fontSize: "0.85rem",
                border: "1px solid #d0d0d0",
                borderRadius: "4px",
              }}
            >
              <option value="">All types</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex gap-1">
            {DATE_RANGES.map((r) => (
              <button
                key={r.label}
                type="button"
                className={`btn btn--sm ${dateRange === r.days ? "btn--primary" : "btn--secondary"}`}
                onClick={() => handleDateChange(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="d-flex gap-1" style={{ marginLeft: "auto" }}>
            {(["revenue", "customers", "payments"] as const).map((type) => (
              <button
                key={type}
                type="button"
                className="btn btn--secondary btn--sm text-xs"
                disabled={downloading !== null}
                onClick={() => handleExport(type)}
              >
                {downloading === type ? "…" : `${type[0].toUpperCase() + type.slice(1)} CSV`}
              </button>
            ))}
          </div>
        </div>

        {isLoading && <Skeleton lines={3} />}
        {data && (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Stripe ID</th>
                  <th>Occurred</th>
                  <th className="text-center">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.events.map((event) => (
                  <Fragment key={event.id}>
                    <tr>
                      <td
                        style={{ fontFamily: "monospace" }}
                        className="text-sm"
                      >
                        {event.eventType}
                      </td>
                      <td
                        style={{ fontFamily: "monospace" }}
                        className="text-xs text-muted"
                      >
                        {event.stripeEventId.slice(0, 20)}…
                      </td>
                      <td className="text-sm text-muted">
                        {new Date(event.occurredAt).toLocaleString("en-IE")}
                      </td>
                      <td className="text-center">
                        {event.processed ? (
                          <span className="text-success">✓</span>
                        ) : event.errorMessage ? (
                          <span
                            className="text-danger"
                            style={{ cursor: "help" }}
                            title={event.errorMessage}
                          >
                            ✗
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm text-xs"
                            onClick={() =>
                              setExpandedId(
                                expandedId === event.id ? null : event.id,
                              )
                            }
                          >
                            {expandedId === event.id ? "Hide" : "Payload"}
                          </button>
                          {!event.processed && event.errorMessage && (
                            <button
                              type="button"
                              className="btn btn--primary btn--sm text-xs"
                              disabled={replayMutation.isPending}
                              onClick={() => {
                                if (window.confirm("Replay this failed billing event? This will re-process the Stripe webhook payload.")) {
                                  replayMutation.mutate(event.id);
                                }
                              }}
                            >
                              {replayMutation.isPending ? "\u2026" : "Replay"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === event.id && (
                      <tr key={`${event.id}-payload`}>
                        <td
                          colSpan={5}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          {"payloadJson" in event && (event as any).payloadJson ? (
                            <pre
                              style={{
                                margin: 0,
                                fontSize: "0.8rem",
                                maxHeight: "300px",
                                overflow: "auto",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {(() => {
                                try {
                                  return JSON.stringify(
                                    JSON.parse((event as any).payloadJson),
                                    null,
                                    2,
                                  );
                                } catch {
                                  return (event as any).payloadJson;
                                }
                              })()}
                            </pre>
                          ) : (
                            <span className="text-muted text-sm">
                              Payload not available in list view.
                            </span>
                          )}
                          {event.errorMessage && (
                            <div
                              className="text-danger text-sm mt-1"
                              style={{
                                padding: "8px",
                                backgroundColor: "#fff3f3",
                                borderRadius: "4px",
                              }}
                            >
                              Error: {event.errorMessage}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {data.events.length === 0 && (
              <p className="text-muted text-center" style={{ padding: "24px" }}>
                No billing events found.
              </p>
            )}
            <div className="d-flex gap-2 items-center mt-3">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="text-muted text-sm">
                Page {page} · {data.total} total
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={data.events.length < 50}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
